import { startOfDay, endOfDay, format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { ptBR } from "date-fns/locale";

const getUserTimezone = () => {
    const userData = JSON.parse(localStorage.getItem("__hidroview__me") || "{}");
    return userData.timezone || "UTC";
};

export const normalizeToDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) return value;

    const timestamp = Number(value);
    if (!isNaN(timestamp)) {
        return new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
    }

    if (typeof value === "string") {
        const parts = value.match(/\d+/g);
        if (!parts || parts.length < 3) return null;

        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);

        return new Date(year, month, day);
    }

    return null;
};

export const normalizePeriodToDate = (period = {}) => {
    return {
        startDate: normalizeToDate(period.startDate) || new Date(),
        endDate: normalizeToDate(period.endDate) || new Date()
    };
};

const buildTimestamp = (date, endOfDayFlag = false) => {
    const timeZone = getUserTimezone();
    const adjustedDate = endOfDayFlag ? endOfDay(date) : startOfDay(date);
    const utcDate = zonedTimeToUtc(adjustedDate, timeZone);
    return Math.floor(utcDate.getTime() / 1000);
};

export const normalizeToTimestamp = (startDate, endDate) => {
    const start = normalizeToDate(startDate);
    const end = normalizeToDate(endDate);

    if (!start || !end) return null;

    return {
        startDate: buildTimestamp(start, false),
        endDate: buildTimestamp(end, true)
    };
};

export const isSameDay = (startDate, endDate) => {
    const start = normalizeToDate(startDate);
    const end = normalizeToDate(endDate);
    if (!start || !end) return false;

    return (
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate()
    );
};

export const formatDate = (date, locale = "pt-BR") => {
    const normalized = normalizeToDate(date);
    if (!normalized) return "";
    return normalized.toLocaleDateString(locale);
};

export const formatPeriod = (startDate, endDate, locale = "pt-BR") => {
    if (isSameDay(startDate, endDate)) return formatDate(startDate, locale);
    return `${formatDate(startDate, locale)} - ${formatDate(endDate, locale)}`;
};

export const getDaysDiff = (startDate, endDate) => {
    const start = normalizeToDate(startDate);
    const end = normalizeToDate(endDate);
    if (!start || !end) return 0;

    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
};

export const formatPeriodLabel = (startTs, endTs) => {
    if (!startTs || !endTs) return "";

    const timeZone = getUserTimezone();

    const startDate = utcToZonedTime(startTs * 1000, timeZone);
    const endDate = utcToZonedTime(endTs * 1000, timeZone);

    return `${format(startDate, "dd/MM/yyyy", { timeZone })} - ${format(endDate, "dd/MM/yyyy", { timeZone })}`;
};

export const formatValueLabel = (value, isCompare = false) => {
    if (!value) return "";

    if (isCompare && value.firstPeriod && value.secondPeriod) {
        const first = formatPeriodLabel(value.firstPeriod.startDate, value.firstPeriod.endDate);
        const second = formatPeriodLabel(value.secondPeriod.startDate, value.secondPeriod.endDate);
        return `${first} | ${second}`;
    }

    if (value.startDate && value.endDate) {
        return formatPeriodLabel(value.startDate, value.endDate);
    }

    return "";
};

export const toIsoDateWithoutTime = (timestampInSeconds, useUserTimezone = false) => {
    if (!timestampInSeconds) return "";

    const dateMs = timestampInSeconds * 1000;

    if (!useUserTimezone) {
        const date = new Date(dateMs);
        return date.toISOString().split("T")[0];
    }

    const timeZone = getUserTimezone();
    const zonedDate = utcToZonedTime(dateMs, timeZone);

    return format(zonedDate, "yyyy-MM-dd", { timeZone });
};

export const setTimeInTimestamp = (timestampInSeconds, { hours = 0, minutes = 0, seconds = 0 }, useUserTimezone = true) => {
    if (!timestampInSeconds) return null;

    const dateMs = timestampInSeconds * 1000;
    const timeZone = getUserTimezone();

    const zoned = useUserTimezone ? utcToZonedTime(dateMs, timeZone) : new Date(dateMs);

    const adjusted = new Date(
        zoned.getFullYear(),
        zoned.getMonth(),
        zoned.getDate(),
        hours,
        minutes,
        seconds,
        0
    );

    const finalDate = useUserTimezone ? zonedTimeToUtc(adjusted, timeZone) : adjusted;

    return Math.floor(finalDate.getTime() / 1000);
};

export const formatTimestampLabel = (timestampInSeconds, pattern = "dd/MM/yyyy") => {
    if (!timestampInSeconds) return "";

    const timeZone = getUserTimezone();

    const zonedDate = utcToZonedTime(timestampInSeconds * 1000, timeZone);

    return format(zonedDate, pattern, { timeZone, locale: ptBR });
};